import React, { useState, useEffect } from 'react';
import { useDashboard } from './DashboardContext';
import { AlertTriangle, Upload, RefreshCw, Bell, CheckCircle2, XCircle, Video, Image as ImageIcon, User, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hyperdriveToken')}` });

const MediaRestorationPage = () => {
  const { user } = useDashboard();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState({}); // keyed by episode_id/course_id
  const [banner, setBanner] = useState(null);

  const showBanner = (type, text) => {
    setBanner({ type, text });
    setTimeout(() => setBanner(null), 6000);
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/media/restoration-center`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const uploadFile = async (file, kind) => {
    // Videos: use the chunked-upload flow via uploadApi.uploadVideo so large
    // files (>25MB) don't hit proxy body-size limits.
    if (kind === 'video') {
      const { uploadApi } = await import('../../services/api');
      return uploadApi.uploadVideo(file);
    }
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${API}/api/upload/${kind}`, { method: 'POST', body: form });
    if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
    return res.json();
  };

  const restoreEpisode = async (row, file) => {
    const key = `ep-${row.episode_id}`;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const uploaded = await uploadFile(file, 'video');
      const res = await fetch(`${API}/api/admin/media/restore-episode/${row.course_id}/${row.episode_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ new_video_url: uploaded.url, new_file_id: uploaded.id }),
      });
      if (!res.ok) throw new Error(`Restore failed: HTTP ${res.status}`);
      showBanner('success', `Restored "${row.episode_title}" ✓`);
      await load();
    } catch (e) {
      showBanner('error', `Failed to restore ${row.episode_title}: ${e.message}`);
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  };

  const restoreThumbnail = async (row, file) => {
    const key = `th-${row.course_id}`;
    setUploading(u => ({ ...u, [key]: true }));
    try {
      const uploaded = await uploadFile(file, 'image');
      const res = await fetch(`${API}/api/admin/media/restore-thumbnail/${row.course_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ new_thumbnail_url: uploaded.url }),
      });
      if (!res.ok) throw new Error(`Restore failed: HTTP ${res.status}`);
      showBanner('success', `Restored thumbnail for "${row.course_title}" ✓`);
      await load();
    } catch (e) {
      showBanner('error', `Failed: ${e.message}`);
    } finally {
      setUploading(u => ({ ...u, [key]: false }));
    }
  };

  const notifyAllUsers = async () => {
    if (!window.confirm(`Send re-upload prompt to ${data.counts.broken_profile_pics} user(s)?`)) return;
    setUploading(u => ({ ...u, notify: true }));
    try {
      const res = await fetch(`${API}/api/admin/media/notify-broken-profile-pics`, {
        method: 'POST', headers: authHeaders(),
      });
      const result = await res.json();
      showBanner('success', result.message);
    } catch (e) {
      showBanner('error', `Failed: ${e.message}`);
    } finally {
      setUploading(u => ({ ...u, notify: false }));
    }
  };

  if (!user || user.role !== 'admin') {
    return <div className="page-content"><h1 className="page-title">Access Denied</h1><p>Admin only.</p></div>;
  }

  if (loading) {
    return (
      <div className="page-content" data-testid="media-restoration-loading">
        <h1 className="page-title">Media Restoration Center</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: '#888' }}>
          <Loader2 size={20} className="spin" /> Scanning for broken media…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <h1 className="page-title">Media Restoration Center</h1>
        <div style={{ background: '#3a1a1a', border: '1px solid #a33', padding: 16, borderRadius: 8, color: '#faa' }}>
          <AlertTriangle size={20} /> Failed to load: {error}
          <button onClick={load} style={{ marginLeft: 12 }} className="btn-secondary">Retry</button>
        </div>
      </div>
    );
  }

  const { counts, broken_episodes, broken_thumbnails, broken_profile_pics, s3_alive } = data;
  const totalBroken = counts.broken_episodes + counts.broken_thumbnails + counts.broken_profile_pics;

  return (
    <div className="page-content" data-testid="media-restoration-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Media Restoration Center</h1>
        <button className="btn-secondary" onClick={load} data-testid="reload-btn">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {banner && (
        <div
          data-testid="banner"
          style={{
            padding: 12, borderRadius: 8, marginBottom: 16,
            background: banner.type === 'success' ? '#0a2e1a' : '#3a1a1a',
            border: `1px solid ${banner.type === 'success' ? '#0a6' : '#a33'}`,
            color: banner.type === 'success' ? '#8f8' : '#faa',
            display: 'flex', gap: 10, alignItems: 'center',
          }}>
          {banner.type === 'success' ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          {banner.text}
        </div>
      )}

      <div style={{
        padding: 16, marginBottom: 24, borderRadius: 8,
        background: s3_alive ? '#0a2e1a' : '#3a1a1a',
        border: `1px solid ${s3_alive ? '#0a6' : '#a33'}`,
      }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', color: s3_alive ? '#8f8' : '#faa' }}>
          {s3_alive ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
          <strong>AWS S3:</strong> {s3_alive ? 'Reachable' : 'DEAD — all S3-stored media is unrecoverable via the current credentials'}
        </div>
        <p style={{ margin: '8px 0 0', color: '#aaa', fontSize: 14 }}>
          New uploads now go to Emergent Object Storage (persistent, no AWS bill). Re-upload broken items below.
        </p>
      </div>

      {totalBroken === 0 && (
        <div data-testid="all-clear" style={{ padding: 24, textAlign: 'center', background: '#0a2e1a', border: '1px solid #0a6', borderRadius: 8, color: '#8f8' }}>
          <CheckCircle2 size={32} style={{ margin: '0 auto 8px', display: 'block' }} />
          <h3 style={{ margin: 0 }}>Nothing broken 🎉</h3>
          <p style={{ margin: '4px 0 0', color: '#aaa' }}>All course videos, thumbnails and profile pictures are accessible.</p>
        </div>
      )}

      {counts.broken_episodes > 0 && (
        <section style={{ marginBottom: 32 }} data-testid="broken-episodes-section">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Video size={22} /> Broken Course Videos <span style={{ color: '#faa', fontSize: 14 }}>({counts.broken_episodes})</span>
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {broken_episodes.map(row => {
              const key = `ep-${row.episode_id}`;
              return (
                <div key={key} data-testid={`broken-episode-${row.episode_id}`}
                  style={{ padding: 16, background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontWeight: 600 }}>{row.course_title}</div>
                    <div style={{ color: '#ccc' }}>{row.section_title} → {row.episode_title}</div>
                    <div style={{ color: '#faa', fontSize: 13, marginTop: 4 }}>{row.reason}</div>
                  </div>
                  <label className="btn-primary" style={{ cursor: uploading[key] ? 'wait' : 'pointer' }}>
                    {uploading[key] ? <><Loader2 size={16} className="spin" /> Uploading…</> : <><Upload size={16} /> Re-upload video</>}
                    <input type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" hidden
                      disabled={uploading[key]}
                      data-testid={`upload-input-${row.episode_id}`}
                      onChange={e => e.target.files[0] && restoreEpisode(row, e.target.files[0])} />
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {counts.broken_thumbnails > 0 && (
        <section style={{ marginBottom: 32 }} data-testid="broken-thumbnails-section">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ImageIcon size={22} /> Broken Course Thumbnails <span style={{ color: '#faa', fontSize: 14 }}>({counts.broken_thumbnails})</span>
          </h2>
          <div style={{ display: 'grid', gap: 12 }}>
            {broken_thumbnails.map(row => {
              const key = `th-${row.course_id}`;
              return (
                <div key={key} data-testid={`broken-thumbnail-${row.course_id}`}
                  style={{ padding: 16, background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontWeight: 600 }}>{row.course_title}</div>
                    <div style={{ color: '#faa', fontSize: 13, marginTop: 4 }}>{row.reason}</div>
                  </div>
                  <label className="btn-primary" style={{ cursor: uploading[key] ? 'wait' : 'pointer' }}>
                    {uploading[key] ? <><Loader2 size={16} className="spin" /> Uploading…</> : <><Upload size={16} /> Re-upload thumbnail</>}
                    <input type="file" accept="image/*" hidden disabled={uploading[key]}
                      data-testid={`thumb-input-${row.course_id}`}
                      onChange={e => e.target.files[0] && restoreThumbnail(row, e.target.files[0])} />
                  </label>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {counts.broken_profile_pics > 0 && (
        <section style={{ marginBottom: 32 }} data-testid="broken-profile-pics-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
              <User size={22} /> Broken Profile Pictures <span style={{ color: '#faa', fontSize: 14 }}>({counts.broken_profile_pics})</span>
            </h2>
            <button className="btn-primary" onClick={notifyAllUsers} disabled={uploading.notify} data-testid="notify-all-btn">
              {uploading.notify ? <><Loader2 size={16} className="spin" /> Sending…</> : <><Bell size={16} /> Notify all to re-upload</>}
            </button>
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {broken_profile_pics.map(row => (
              <div key={row.user_id} data-testid={`broken-profile-${row.user_id}`}
                style={{ padding: 12, background: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }}>
                <div style={{ fontWeight: 600 }}>{row.user_name || '(no name)'}</div>
                <div style={{ color: '#ccc', fontSize: 13 }}>{row.user_email}</div>
                <div style={{ color: '#faa', fontSize: 12, marginTop: 4 }}>{row.reason}</div>
              </div>
            ))}
          </div>
          <p style={{ color: '#888', fontSize: 13, marginTop: 12 }}>
            Users can only re-upload their own profile pictures. Click &quot;Notify all&quot; to send them a prompt.
          </p>
        </section>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default MediaRestorationPage;
